/**
 * The Venom Altar walkable geometry (v2, docs/design/dungeon-layout-study.md §5): a serpent-cult
 * monastery cut into a jungle cliff. Original layout; its proportions follow the classic-instance
 * study (enclosed halls 36–84 yd, 8–12 yd corridors 20–30 yd long between them, an open cloister
 * courtyard, a timbered mine wing, big sealed boss halls as landmarks, four floor levels).
 *
 *   gate ─ cloister ─ ravager_pit ─ scriptorium ─ hatchery ─ coil_cistern ─ venom_crypt ─┬─ treasury
 *                                                                                      └─ fang_stair ─ fang_altar
 *
 * Coordinates are yards (x east, y north = away from the default camera). Rooms are
 * axis-aligned boxes; corridors are straight strips whose centrelines start/end inside the rooms.
 * Pack compositions, forces and the route order are unchanged from v1 (Altar of Fangs shape).
 */
import type { CorridorDef, DoorDef, Polygon, PropHint, Rect, RoomDef, Vec2 } from '@keystone/sim';
import { ENCOUNTER } from '../ids';
import { doorAcross, riseBetween } from './elevation';
import { mapScale } from './geometry';

export const YARDS_PER_UNIT = 1;
export const M = mapScale(YARDS_PER_UNIT);

/**
 * Floor levels (yards, visual only): L0 the sunken pit / flooded cistern and crypt, L1 the gate,
 * cloister, mine hatchery and treasury, L2 the scriptorium and the grand stair, L3 the altar summit.
 */
export const LEVEL = { L0: 0, L1: 2, L2: 4, L3: 6 } as const;

const ROOM_BOXES = {
  gate: { name: 'Shedding Gate', box: [130, 0, 166, 46], elevation: LEVEL.L1 }, // vaulted narthex, first two packs
  cloister: { name: 'Serpent Cloister', box: [116, 76, 180, 140], elevation: LEVEL.L1 }, // open courtyard + arcade
  ravager_pit: { name: "Ravager's Pit", box: [12, 68, 92, 136], elevation: LEVEL.L0 }, // boss 1 (sunken beast pit)
  scriptorium: { name: 'Scale Scriptorium', box: [16, 166, 100, 206], elevation: LEVEL.L2 }, // long library hall
  hatchery: { name: 'Writhing Hatchery', box: [124, 176, 170, 222], elevation: LEVEL.L1 }, // timbered mine cavern
  coil_cistern: { name: 'Coil Cistern', box: [198, 166, 250, 238], elevation: LEVEL.L0 }, // boss 2 (flooded cistern)
  venom_crypt: { name: 'Venom Crypt', box: [196, 258, 256, 306], elevation: LEVEL.L0 }, // poison-pool crypt
  treasury: { name: 'Ascendant Treasury', box: [276, 266, 312, 302], elevation: LEVEL.L1 }, // side room (gold tease)
  fang_stair: { name: 'Fang Stair', box: [198, 330, 254, 370], elevation: LEVEL.L2 }, // ritual stair hall
  fang_altar: { name: 'The Fang Altar', box: [191, 390, 261, 446], elevation: LEVEL.L3 }, // boss 3 (summit hall)
} as const satisfies Record<string, { name: string; box: readonly [number, number, number, number]; elevation: number }>;

export type VenomRoomId = keyof typeof ROOM_BOXES;

export const ROOMS: RoomDef[] = (Object.keys(ROOM_BOXES) as VenomRoomId[]).map((id) => {
  const { name, box, elevation } = ROOM_BOXES[id];
  return { id, name, shape: M.rectPoly(box[0], box[1], box[2], box[3]), elevation };
});

const roomRect = (id: VenomRoomId): Rect => {
  const [x0, y0, x1, y1] = ROOM_BOXES[id].box;
  return M.rect(x0, y0, x1, y1);
};

interface CorridorSpec {
  id: string;
  from: VenomRoomId;
  to: VenomRoomId;
  /** Centreline endpoints (inside each room so the nav grid joins them). */
  a: [number, number];
  b: [number, number];
  width: number;
  /** Put the flight in the last N yards before `to` (after any landing) instead of the whole gap. */
  stairsLastYards?: number;
  /** Flat yards at the top of the flight before the `to` room (a landing for a door). */
  landing?: number;
  /** Mine tunnels slope instead of stepping. */
  ramp?: boolean;
}

const CORRIDOR_SPECS: CorridorSpec[] = [
  // Processional gallery: 30 yd, arched every few yards.
  { id: 'c_gate_cloister', from: 'gate', to: 'cloister', a: [148, 42], b: [148, 80], width: 9 },
  // Down into the pit: flat, then a flight in the last 8 yd.
  { id: 'c_cloister_pit', from: 'cloister', to: 'ravager_pit', a: [120, 108], b: [88, 108], width: 10, stairsLastYards: 8 },
  // The long climb out of the pit to the scriptorium (4 yd), with a landing for the hall door.
  { id: 'c_pit_scriptorium', from: 'ravager_pit', to: 'scriptorium', a: [58, 132], b: [58, 170], width: 10, stairsLastYards: 14, landing: 5 },
  // Mine adit: a timbered tunnel ramping down into the hatchery.
  { id: 'c_scriptorium_hatchery', from: 'scriptorium', to: 'hatchery', a: [96, 196], b: [128, 196], width: 8, ramp: true },
  { id: 'c_hatchery_cistern', from: 'hatchery', to: 'coil_cistern', a: [166, 200], b: [202, 200], width: 8, ramp: true },
  { id: 'c_cistern_crypt', from: 'coil_cistern', to: 'venom_crypt', a: [224, 234], b: [224, 262], width: 10 },
  { id: 'c_crypt_treasury', from: 'venom_crypt', to: 'treasury', a: [252, 284], b: [280, 284], width: 8, stairsLastYards: 8 },
  // The grand stair: 4 yd up out of the crypt, landing for the portcullis.
  { id: 'c_crypt_stair', from: 'venom_crypt', to: 'fang_stair', a: [226, 302], b: [226, 334], width: 10, stairsLastYards: 14, landing: 5 },
  { id: 'c_stair_altar', from: 'fang_stair', to: 'fang_altar', a: [226, 366], b: [226, 394], width: 12, stairsLastYards: 10, landing: 6 },
];

const specById = new Map(CORRIDOR_SPECS.map((c) => [c.id, c]));
const centreline = (c: CorridorSpec): [Vec2, Vec2] => [M.pt(c.a[0], c.a[1]), M.pt(c.b[0], c.b[1])];

/**
 * Corridors. Where the two rooms sit at different levels the corridor carries a stair flight (or a
 * ramp in the mine) between the room edges, so flights always line up with the room floors.
 */
export const CORRIDORS: CorridorDef[] = CORRIDOR_SPECS.map((c) => {
  const [a, b] = centreline(c);
  const def: CorridorDef = { id: c.id, from: c.from, to: c.to, shape: M.strip(c.a, c.b, c.width) };
  const rise = riseBetween(a, b, roomRect(c.from), roomRect(c.to), ROOM_BOXES[c.from].elevation, ROOM_BOXES[c.to].elevation, {
    stripWidth: c.width,
    ...(c.ramp ? { kind: 'ramp' as const } : {}),
    ...(c.stairsLastYards !== undefined ? { lastYards: c.stairsLastYards } : {}),
    ...(c.landing !== undefined ? { landing: c.landing } : {}),
  });
  if (rise) def.rises = [rise];
  return def;
});

type DoorSpec = { id: string; corridor: string; at: 'from' | 'to'; style: DoorDef['style']; seals?: string; minGap?: number; depth?: number };

/**
 * Doors. Open gates/portcullises are set dressing the sim ignores while open; the three boss
 * seals close behind the party on encounter start and open on kill/wipe.
 */
const DOOR_SPECS: DoorSpec[] = [
  { id: 'd_cloister_gate', corridor: 'c_gate_cloister', at: 'to', style: 'wood' },
  { id: 'd_ravager_east', corridor: 'c_cloister_pit', at: 'to', style: 'portcullis', seals: ENCOUNTER.ravager },
  { id: 'd_ravager_north', corridor: 'c_pit_scriptorium', at: 'from', style: 'portcullis', seals: ENCOUNTER.ravager },
  { id: 'd_scriptorium_gate', corridor: 'c_pit_scriptorium', at: 'to', style: 'wood' },
  { id: 'd_coil_west', corridor: 'c_hatchery_cistern', at: 'to', style: 'vines', seals: ENCOUNTER.coil },
  { id: 'd_coil_north', corridor: 'c_cistern_crypt', at: 'from', style: 'vines', seals: ENCOUNTER.coil },
  { id: 'd_stair_portcullis', corridor: 'c_crypt_stair', at: 'to', style: 'portcullis' },
  { id: 'd_chieftain_seal', corridor: 'c_stair_altar', at: 'to', style: 'stone', seals: ENCOUNTER.chieftain },
];

export const DOORS: DoorDef[] = DOOR_SPECS.map((d) => {
  const c = specById.get(d.corridor)!;
  const [a, b] = centreline(c);
  return doorAcross({
    id: d.id,
    corridorId: c.id,
    a,
    b,
    fromRoom: c.from,
    toRoom: c.to,
    fromRect: roomRect(c.from),
    toRect: roomRect(c.to),
    stripWidth: c.width,
    at: d.at,
    style: d.style,
    ...(d.seals ? { sealsEncounter: d.seals } : {}),
    ...(d.minGap !== undefined ? { minGap: d.minGap } : {}),
    ...(d.depth !== undefined ? { depth: d.depth } : {}),
  });
});

// ---- obstacles: the cloister colonnade and its fountain --------------------------------------

/** The cloister garth (open-air garden) inside the arcade; the colonnade stands on its edge. */
export const CLOISTER_GARTH: Rect = M.rect(126, 86, 170, 130);
/** Covered arcade walk width around the garth (yd). */
export const CLOISTER_WALK = 10;

const square = (c: Vec2, h: number): Polygon => ({
  points: [
    { x: c.x - h, y: c.y - h },
    { x: c.x + h, y: c.y - h },
    { x: c.x + h, y: c.y + h },
    { x: c.x - h, y: c.y + h },
  ],
});
const octagon = (c: Vec2, r: number): Polygon => ({
  points: Array.from({ length: 8 }, (_, k) => ({ x: Math.round((c.x + Math.cos(((k + 0.5) * Math.PI) / 4) * r) * 100) / 100, y: Math.round((c.y + Math.sin(((k + 0.5) * Math.PI) / 4) * r) * 100) / 100 })),
});

/**
 * Colonnade pillars around the garth: every 7.3 yd along each side, with the four corner piers and
 * a gap where each walk crosses into the garth (the corridor axes and the routes between packs).
 */
export const COLONNADE: Vec2[] = (() => {
  const g = CLOISTER_GARTH;
  const out: Vec2[] = [];
  const xs = [0, 1, 2, 3, 4, 5, 6].map((i) => g.x + (g.w * i) / 6);
  const ys = [0, 1, 2, 3, 4, 5, 6].map((i) => g.y + (g.h * i) / 6);
  // Openings: the middle bay of every side stays open (gallery in from the south, pit stair to the west).
  for (const x of xs) {
    if (Math.abs(x - (g.x + g.w / 2)) < 1) continue;
    out.push({ x: Math.round(x * 10) / 10, y: g.y }, { x: Math.round(x * 10) / 10, y: g.y + g.h });
  }
  for (const y of ys.slice(1, -1)) {
    if (Math.abs(y - (g.y + g.h / 2)) < 1) continue;
    out.push({ x: g.x, y: Math.round(y * 10) / 10 }, { x: g.x + g.w, y: Math.round(y * 10) / 10 });
  }
  return out;
})();

/** Serpent fountain in the middle of the garth (the courtyard landmark). */
export const FOUNTAIN: Vec2 = M.pt(148, 108);
export const FOUNTAIN_RADIUS = 3.6;

export const OBSTACLES: Polygon[] = [...COLONNADE.map((p) => square(p, 0.8)), octagon(FOUNTAIN, FOUNTAIN_RADIUS)];

// ---- authored hero props ------------------------------------------------------------------------

type Side = 'n' | 's' | 'e' | 'w';
/**
 * A point on the dressing ledge just outside a room wall: `side`, fraction `t` along it (west→east
 * or south→north), `out` yards past the floor edge. Ledge props face the room (arch.ts).
 */
function onWall(room: VenomRoomId, side: Side, t: number, out = 1.6): Vec2 {
  const [x0, y0, x1, y1] = ROOM_BOXES[room].box;
  const r1 = (v: number): number => Math.round(v * 10) / 10;
  if (side === 'n') return { x: r1(x0 + (x1 - x0) * t), y: y1 + out };
  if (side === 's') return { x: r1(x0 + (x1 - x0) * t), y: y0 - out };
  if (side === 'e') return { x: x1 + out, y: r1(y0 + (y1 - y0) * t) };
  return { x: x0 - out, y: r1(y0 + (y1 - y0) * t) };
}
const FACE: Record<Side, number> = { n: -Math.PI / 2, s: Math.PI / 2, e: Math.PI, w: 0 };
const hint = (kind: string, room: VenomRoomId, side: Side, t: number, extra: Partial<PropHint> = {}, out?: number): PropHint => ({
  kind,
  pos: onWall(room, side, t, out),
  facing: FACE[side],
  roomId: room,
  ...extra,
});

/** A point 1 yd outside a room edge along a corridor (for archway hints). */
const mouth = (corridorId: string, at: 'from' | 'to'): Vec2 => {
  const c = specById.get(corridorId)!;
  const [a, b] = centreline(c);
  const r = roomRect(at === 'from' ? c.from : c.to);
  const [p, q] = at === 'from' ? [a, b] : [b, a];
  const len = Math.hypot(q.x - p.x, q.y - p.y);
  const ux = (q.x - p.x) / len;
  const uy = (q.y - p.y) / len;
  let s = 0;
  while (s < len && p.x + ux * s >= r.x && p.x + ux * s <= r.x + r.w && p.y + uy * s >= r.y && p.y + uy * s <= r.y + r.h) s += 0.1;
  s += 1;
  return { x: Math.round((p.x + ux * s) * 10) / 10, y: Math.round((p.y + uy * s) * 10) / 10 };
};
const axis = (corridorId: string): number => {
  const [a, b] = centreline(specById.get(corridorId)!);
  return Math.round(Math.atan2(b.y - a.y, b.x - a.x) * 1e4) / 1e4;
};
const archAt = (kind: 'arch' | 'arch_gold', corridorId: string, at: 'from' | 'to'): PropHint => ({ kind, pos: mouth(corridorId, at), facing: axis(corridorId), corridorId });

/**
 * Authored hero props (renderer-only). Every hint sits on a dressing ledge outside the walkable
 * area, is a flat floor decal, marks a corridor opening (arch / arch_gold), or stands inside an
 * obstacle (the fountain), so none of them blocks anything. The renderer scatters the clutter.
 */
export const PROP_HINTS: PropHint[] = [
  // Shedding Gate: the cliff gate behind the spawn, cobra guardians at the gallery mouth, braziers.
  hint('ruined_gate', 'gate', 's', 0.5),
  { kind: 'cobra_statue', pos: { x: 140.5, y: 47.6 }, facing: -Math.PI / 2, roomId: 'gate' },
  { kind: 'cobra_statue', pos: { x: 155.5, y: 47.6 }, facing: -Math.PI / 2, roomId: 'gate' },
  hint('brazier', 'gate', 'w', 0.3),
  hint('brazier', 'gate', 'e', 0.3),
  hint('brazier', 'gate', 'w', 0.75),
  hint('brazier', 'gate', 'e', 0.75),
  archAt('arch', 'c_gate_cloister', 'from'),
  // Serpent Cloister: the fountain (inside its obstacle), egg clutches and cages in the arcade.
  { kind: 'serpent_fountain', pos: FOUNTAIN, facing: Math.PI / 2, roomId: 'cloister' },
  hint('cage', 'cloister', 'e', 0.3),
  hint('cage', 'cloister', 'e', 0.75),
  hint('egg_clutch', 'cloister', 'n', 0.25),
  hint('egg_clutch', 'cloister', 'n', 0.75),
  hint('cobra_statue', 'cloister', 'w', 0.2),
  hint('cobra_statue', 'cloister', 'w', 0.8),
  archAt('arch', 'c_cloister_pit', 'from'),
  // Ravager's Pit: carcass heap and skull mound on the far ledge, stakes, braziers in the corners.
  hint('ribcage', 'ravager_pit', 'n', 0.3, { scale: 1.6 }),
  hint('skull_mound', 'ravager_pit', 'n', 0.72),
  hint('cage', 'ravager_pit', 'w', 0.3),
  hint('cage', 'ravager_pit', 'w', 0.7),
  hint('brazier', 'ravager_pit', 's', 0.08),
  hint('brazier', 'ravager_pit', 's', 0.92),
  hint('brazier', 'ravager_pit', 'n', 0.08),
  hint('brazier', 'ravager_pit', 'n', 0.92),
  // Scale Scriptorium: bookcases along both long walls, reading desks, a war table at the east end.
  ...[0.12, 0.27, 0.42, 0.72, 0.87].map((t) => hint('bookcase', 'scriptorium', 'n', t)),
  ...[0.12, 0.27, 0.62, 0.77, 0.92].map((t) => hint('bookcase', 'scriptorium', 's', t)),
  hint('lectern', 'scriptorium', 'n', 0.57),
  hint('lectern', 'scriptorium', 's', 0.4),
  hint('weapon_rack', 'scriptorium', 'w', 0.5),
  // Hatchery (mine wing): ore carts on rails, egg clutches, timber props.
  hint('mine_cart', 'hatchery', 'n', 0.3),
  hint('mine_cart', 'hatchery', 'w', 0.25),
  hint('egg_clutch', 'hatchery', 'n', 0.7),
  hint('egg_clutch', 'hatchery', 'e', 0.75),
  hint('egg_clutch', 'hatchery', 's', 0.3),
  hint('ore_pile', 'hatchery', 's', 0.75),
  archAt('arch', 'c_scriptorium_hatchery', 'to'),
  // Coil Cistern: the coiled serpent on the east ledge, floor mosaic, jade statues.
  hint('coiled_serpent', 'coil_cistern', 'e', 0.5, { scale: 1.5 }),
  { kind: 'floor_mosaic', pos: { x: 224, y: 206 }, scale: 14, roomId: 'coil_cistern' },
  hint('cobra_statue', 'coil_cistern', 'w', 0.85),
  hint('cobra_statue', 'coil_cistern', 'n', 0.2),
  hint('cobra_statue', 'coil_cistern', 'n', 0.8),
  // Venom Crypt: poison-water alcoves (W × 2, E × 1), sarcophagi.
  hint('poison_pool', 'venom_crypt', 'w', 0.3),
  hint('poison_pool', 'venom_crypt', 'w', 0.78),
  hint('poison_pool', 'venom_crypt', 'e', 0.14),
  hint('sarcophagus', 'venom_crypt', 'n', 0.18),
  hint('sarcophagus', 'venom_crypt', 'n', 0.82),
  archAt('arch_gold', 'c_crypt_treasury', 'to'),
  // Ascendant Treasury: gilded idol, braziers flanking, gold pile.
  hint('idol', 'treasury', 'e', 0.5, { scale: 1.4 }),
  hint('brazier', 'treasury', 'e', 0.2),
  hint('brazier', 'treasury', 'e', 0.8),
  hint('gold_pile', 'treasury', 'n', 0.5),
  hint('gold_pile', 'treasury', 's', 0.4),
  // Fang Stair: war totems flanking the upper flight, drums, braziers.
  { kind: 'war_totem', pos: { x: 216.5, y: 371.6 }, roomId: 'fang_stair' },
  { kind: 'war_totem', pos: { x: 235.5, y: 371.6 }, roomId: 'fang_stair' },
  hint('drum', 'fang_stair', 'w', 0.5),
  hint('drum', 'fang_stair', 'e', 0.5),
  hint('brazier', 'fang_stair', 'w', 0.15),
  hint('brazier', 'fang_stair', 'e', 0.15),
  // The Fang Altar: altar dais + fang jaw behind the boss, four braziers.
  hint('altar_dais', 'fang_altar', 'n', 0.5, { scale: 1.5 }, 1.4),
  hint('fang_jaw', 'fang_altar', 'n', 0.5, { scale: 2 }, 4.5),
  hint('brazier', 'fang_altar', 'w', 0.15),
  hint('brazier', 'fang_altar', 'e', 0.15),
  hint('brazier', 'fang_altar', 'w', 0.85),
  hint('brazier', 'fang_altar', 'e', 0.85),
];

/** Boss arenas (yards). */
export const ARENA = {
  ravager: roomRect('ravager_pit'),
  coil: roomRect('coil_cistern'),
  chieftain: roomRect('fang_altar'),
} as const;

/**
 * Ravager's carcass piles (the Feeding Frenzy leap targets): two by the far wall under the carcass
 * heap and skull mound, one in the near-west corner. Same arena-relative spots as v1.
 */
export const RAVAGER_CARCASSES: Vec2[] = [
  { x: 35, y: 129 },
  { x: 70, y: 129 },
  { x: 21.5, y: 79 },
];

/** Whole-map bounds (yards) with a margin. */
export const BOUNDS: Rect = M.rect(4, -10, 324, 458);

/** Run start: just inside the cliff gate. */
export const ENTRANCE = M.pt(148, 8);
